const PRINT_SERVICE_URLS = ["http://127.0.0.1:5000", "http://localhost:5000"];
const STATUS_TIMEOUT_MS = 800;
const REQUEST_TIMEOUT_MS = 8000;

let detectedService = null;
let detectedAt = 0;
let detectionPromise = null;

const notifyStatus = (detail) => {
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(new CustomEvent("printer-status-change", { detail }));
  }
};

const request = async (baseUrl, path, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(`${baseUrl}${path}`, {
      ...options,
      credentials: "omit",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
};

const detectService = async () => {
  if (detectedService && Date.now() - detectedAt < 30000) return detectedService;
  if (detectionPromise) return detectionPromise;

  detectionPromise = (async () => {
    const probes = await Promise.all(PRINT_SERVICE_URLS.map(async (baseUrl) => {
      try {
        const response = await request(baseUrl, "/status", {}, STATUS_TIMEOUT_MS);
        const detail = await response.json();
        return { baseUrl, mode: "cors", responseOk: response.ok, detail };
      } catch (_) {
        return null;
      }
    }));
    detectedService = probes.find(Boolean) || { baseUrl: PRINT_SERVICE_URLS[0], mode: "legacy" };
    detectedAt = Date.now();
    return detectedService;
  })();

  try {
    return await detectionPromise;
  } finally {
    detectionPromise = null;
  }
};

const normalizeTicketData = (source) => {
  const ticket = source?.ticket && typeof source.ticket === "object" ? source.ticket : source;
  if (!ticket || typeof ticket !== "object") return null;

  if (ticket.winner && Number(ticket.winner.amountToPay) > 0) {
    return {
      type: "pago",
      system: "loteria",
      num: String(ticket.num ?? ""),
      amountToPay: Number(ticket.winner.amountToPay),
    };
  }

  if (!ticket.num || !Array.isArray(ticket.betted)) return null;
  return {
    type: "ticket",
    system: "loteria",
    sys: "Q",
    num: String(ticket.num),
    pv: String(ticket.pv || ticket.point?.name || ticket.point || ""),
    date: ticket.date,
    codeRun: ticket.codeRun || ticket.run?.code || ticket.run?.num || ticket.run,
    amount: Number(ticket.amount || 0),
    betted: ticket.betted.map((line) => {
      const numbers = Array.isArray(line?.numbers) ? line.numbers : [];
      const kind = numbers.length === 1 ? "Q" : numbers.length === 2 ? "P" : numbers.length === 3 ? "T" : "";
      return {
        bet: [kind, numbers.join(" ")].filter(Boolean).join(" "),
        amount: Number(line?.betted || 0),
      };
    }),
  };
};

const normalizeSalesReportData = (source) => {
  if (!source || typeof source !== "object") return null;
  return {
    type: "cuadre",
    system: "loteria",
    cuadre: "loteria",
    logo: "loteria",
    point: String(source.point || ""),
    date: String(source.date || "Intervalo no especificado"),
    betted: Number(source.betted || 0),
    winned: Number(source.winned || 0),
    paid: Number(source.paid || 0),
    balance: Number(source.balance || 0),
    deposito: Number(source.deposito || 0),
    retiro: Number(source.retiro || 0),
    balancePlayer: Number(source.balancePlayer || 0),
    total: Number(source.total || 0),
  };
};

const normalizePrinterData = (source) =>
  source?.type === "cuadre"
    ? normalizeSalesReportData(source)
    : normalizeTicketData(source);

const makeIdempotencyKey = (payload) => {
  if (payload.type === "cuadre") return `queen-cuadre-${Date.now()}`;
  const kind = payload.type === "pago" ? "pago" : "ticket";
  return payload.num ? `queen-${kind}-${payload.num}` : `queen-${kind}-${Date.now()}`;
};

const printModern = async (service, payload) => {
  const response = await request(service.baseUrl, "/print", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": makeIdempotencyKey(payload),
    },
    body: JSON.stringify(payload),
  });
  const contentType = response.headers.get("content-type") || "";
  const detail = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok || detail?.printed === false || detail?.success === false || detail?.error) {
    return {
      printed: false,
      online: detail?.online !== false,
      reason: response.status === 409 ? "duplicate" : "print-error",
      status: response.status,
      detail: detail?.msg || detail?.message || detail?.error,
    };
  }
  return { printed: true, online: true, protocol: "cors", detail };
};

const printLegacy = async (service, payload) => {
  // Los agentes antiguos aceptan JSON con Content-Type text/plain y no requieren preflight.
  await request(service.baseUrl, "/print", {
    method: "POST",
    mode: "no-cors",
    keepalive: true,
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify(payload),
  });
  return { printed: true, submitted: true, online: true, protocol: "legacy" };
};

export const ToPrinter = async (source) => {
  const payload = normalizePrinterData(source);
  if (!payload) {
    const result = { printed: false, online: false, reason: "invalid-document" };
    notifyStatus(result);
    return result;
  }

  try {
    const service = await detectService();
    const result = service.mode === "cors"
      ? await printModern(service, payload)
      : await printLegacy(service, payload);
    notifyStatus(result);
    return result;
  } catch (error) {
    const result = {
      printed: false,
      online: false,
      reason: error?.name === "AbortError" ? "timeout" : "service-unavailable",
      detail: error?.message,
    };
    notifyStatus(result);
    return result;
  }
};

export const PrintSalesReport = async (source) =>
  ToPrinter({ ...source, type: "cuadre" });

export const resetPrinterDetection = () => {
  detectedService = null;
  detectedAt = 0;
  detectionPromise = null;
};

export const buildPrinterPayload = normalizePrinterData;
export const buildSalesReportPayload = normalizeSalesReportData;
